import { EditOutlined } from '@ant-design/icons';
import { Heading } from '@mm/components';
import classNames from 'classnames';
import React from 'react';
import { ReactComponent as Stars } from './Icons/stars.svg';
import { ReactComponent as WarningCircle } from './Icons/warning.svg';
import { ReactComponent as WarningRectangle } from './Icons/warning-rectangle.svg';
import { ReactComponent as CurvedBg } from './Icons/timer-curved-bg.svg';
import dashboardSrc from './Icons/dashboard.png';
import leaderLogo from 'assets/LeaderWinter22.png';
import martechLogo from 'assets/best-web-platform.svg';
import EntrepreneurLogo from 'assets/enterprneur-logo-black.svg';
import metaLogo from 'assets/meta-partner.png';
import neilPatel from 'assets/neil-patel-full.svg';
import styles from './WebsiteReportSummary.module.css';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { MissedOpportunities } from '../MissedOpportunities/MissedOpportunities';
import moment from 'moment';
import { MissedLeadsTimer } from '../MissedLeadsTimer/MissedLeadsTimer';
import { useResolutionInfo } from '@mm/hooks';
import { TABLET_SMALL } from '../../../../editor/constants/screenSize';
import { HowXRayWorks } from '../HowXRayWorks/HowXRayWorks';
import { Speedometer } from './Speedometer';
import {
  AVERAGE_WEB_CONVERSION_RATE,
  AVERAGE_WEB_TRAFFIC_GROWTH,
  beautifyNumber,
  HEADER_STEPS,
  REDUCE_COST_PER_LEAD,
} from './constants';
import { useWebsiteReportModals } from './use-website-report-modals';

// tslint:disable-next-line: cyclomatic-complexity
export const WebsiteReportSummary = () => {
  const {
    openSignupModal,
    openEditUrlModal,
    websiteURL,
    totalTrafficRef,
    monthlyTrafficRef,
    busy,
    activeStep,
  } = useWebsiteReportModals();

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const dateNow = moment();

  const organicTrafficTotal =
    totalTrafficRef.current?.trafficTypes?.organic || 0;
  const paidTrafficTotal = totalTrafficRef.current?.trafficTypes?.paid || 0;

  const gathering = 'Gathering...';
  const organicTraffic = busy ? gathering : organicTrafficTotal;
  const paidTraffic = busy ? gathering : paidTrafficTotal;
  const missedOrganicLeads = busy
    ? gathering
    : organicTrafficTotal * AVERAGE_WEB_TRAFFIC_GROWTH;

  const missedTotalLeads = busy
    ? gathering
    : (organicTrafficTotal + paidTrafficTotal) * AVERAGE_WEB_TRAFFIC_GROWTH;

  const hasPaidTraffic = !!(!busy && paidTrafficTotal);

  return (
    <div className={classNames(styles.wrapper)}>
      {!isMobile && (
        <div className={styles.headerContainer}>
          <div className={styles.headerContent}>
            <div className={styles.steps}>
              {HEADER_STEPS.map(item => {
                return (
                  <React.Fragment key={item.step}>
                    <div
                      key={item.step}
                      className={classNames(styles.step, {
                        [styles.inactiveStep]: activeStep !== item.step,
                      })}
                    >
                      {item.title}
                    </div>
                    {item.step !== HEADER_STEPS.length && (
                      <div className={styles.dashes} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div>
        <div className={styles.container}>
          <div className={styles.background}>
            <div className={styles.left} />
            <div className={styles.right} />
          </div>
          <div className={styles.headingWrapper}>
            <Heading size={2} className={styles.heading}>
              Step 2. Estimated email contact capture report for
            </Heading>
            <div className={styles.website}>
              {websiteURL}
              {websiteURL && (
                <EditOutlined
                  className={styles.editIcon}
                  onClick={openEditUrlModal}
                />
              )}
            </div>
          </div>
          <div
            className={
              isMobile
                ? styles.columnReverse
                : classNames(styles.row, styles.summary)
            }
          >
            <div
              className={classNames(styles.column, styles.alignCenter, {
                [styles.left]: !isMobile,
              })}
            >
              <div className={styles.block}>
                <Heading size={3} className={styles.descriptionHeading}>
                  Summary
                </Heading>
                <Heading size={3} className={styles.descriptionHeading}>
                  of missed opportunities
                </Heading>
                <Heading size={3} className={styles.descriptionHeading}>
                  & lost leads
                </Heading>
                <div
                  className={classNames(
                    styles.column,
                    styles.gap40,
                    styles.alignStart,
                  )}
                >
                  <div
                    className={classNames(
                      styles.column,
                      styles.gap8,
                      styles.alignStart,
                    )}
                  >
                    <div className={styles.subtitle}>
                      You have{' '}
                      <span
                        className={classNames(
                          styles.bold600,
                          styles.raggedText,
                        )}
                      >
                        lost {beautifyNumber(missedOrganicLeads)} leads
                      </span>{' '}
                      last month that MobileMonkey could have captured from your
                      traffic.
                    </div>
                    <div className={styles.text}>
                      Based on our analysis, your site has{' '}
                      {beautifyNumber(organicTraffic)} organic visitors per
                      month we could capture an additional{' '}
                      {beautifyNumber(missedOrganicLeads)} email contacts per
                      month from this traffic.
                    </div>
                  </div>
                  {hasPaidTraffic && (
                    <div
                      className={classNames(
                        styles.column,
                        styles.gap8,
                        styles.alignStart,
                      )}
                    >
                      <div
                        className={classNames(
                          styles.subtitle,
                          styles.bold600,
                          styles.raggedText,
                          styles.greenBackground,
                        )}
                      >
                        Reduce your cost per lead to ${REDUCE_COST_PER_LEAD}*
                      </div>
                      <div className={styles.text}>
                        *We estimate your paid traffic at{' '}
                        {beautifyNumber(paidTraffic)} visitors per month
                        resulting in{' '}
                        {typeof paidTraffic === 'number'
                          ? beautifyNumber(
                              paidTraffic * AVERAGE_WEB_CONVERSION_RATE,
                            )
                          : 0}{' '}
                        leads for $100 each on average. Activating MobileMonkey
                        could generate an additional{' '}
                        {typeof paidTraffic === 'number'
                          ? beautifyNumber(
                              paidTraffic * AVERAGE_WEB_TRAFFIC_GROWTH,
                            )
                          : 0}{' '}
                        leads per month, reducing the effective cost per lead to
                        ${REDUCE_COST_PER_LEAD}.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={classNames(styles.column, styles.heroContent, {
                [styles.right]: !isMobile,
              })}
            >
              <div
                className={classNames(
                  styles.column,
                  isMobile ? styles.gap12 : styles.gap24,
                  styles.alignStart,
                  styles.boxesWrapper,
                )}
              >
                <div className={styles.subtitle}>At a glance:</div>
                <div
                  className={classNames(
                    paidTraffic || isMobile ? styles.column : styles.row,
                    isMobile ? styles.gap12 : styles.gap24,
                  )}
                >
                  <div
                    className={classNames(styles.box, {
                      [styles.verticalBox]: !paidTraffic,
                    })}
                  >
                    <div
                      className={classNames(
                        styles.column,
                        styles.gap4,
                        styles.alignStart,
                      )}
                    >
                      <div className={styles.number}>
                        {beautifyNumber(organicTraffic)}
                      </div>
                      <div className={styles.textSmall}>
                        Estimated
                        <div className={styles.textSmall}>Organic Traffic</div>
                      </div>
                    </div>
                    {hasPaidTraffic && (
                      <div
                        className={classNames(
                          styles.column,
                          styles.gap4,
                          styles.alignStart,
                        )}
                      >
                        <div className={styles.number}>
                          {beautifyNumber(paidTraffic)}
                        </div>
                        <div className={styles.textSmall}>
                          Estimated
                          <div className={styles.textSmall}>Paid Traffic</div>
                        </div>
                      </div>
                    )}
                    {!busy && <Speedometer isPoor={false} />}
                  </div>
                  <div
                    className={classNames(styles.box, styles.boxRed, {
                      [styles.verticalBox]: !paidTraffic,
                    })}
                  >
                    <div className={classNames(styles.row, styles.gap12)}>
                      {paidTraffic ? (
                        <WarningCircle />
                      ) : (
                        <WarningRectangle className={styles.warning} />
                      )}
                      <div
                        className={classNames(
                          styles.column,
                          styles.gap4,
                          styles.alignStart,
                        )}
                      >
                        <div className={styles.fatNumber}>
                          {beautifyNumber(missedTotalLeads)}
                        </div>
                        <div
                          className={classNames(
                            styles.textSmall,
                            styles.bold600,
                          )}
                        >
                          Estimated
                          <div
                            className={classNames(
                              styles.textSmall,
                              styles.bold600,
                            )}
                          >
                            Missed Leads / mo
                          </div>
                        </div>
                      </div>
                    </div>
                    {!busy && <Speedometer isPoor />}
                  </div>
                </div>
                <div className={styles.row}>
                  <Stars className={styles.stars} />
                  <PrimaryButton
                    className={styles.button}
                    onClick={openSignupModal}
                  >
                    Capture my {beautifyNumber(missedTotalLeads)} lost leads
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
        <MissedOpportunities
          website={websiteURL}
          isOrganic
          traffic={totalTrafficRef.current}
          trafficByMonth={monthlyTrafficRef.current}
          onClick={openSignupModal}
        />

        {hasPaidTraffic && (
          <MissedOpportunities
            website={websiteURL}
            isOrganic={false}
            traffic={totalTrafficRef.current}
            trafficByMonth={monthlyTrafficRef.current}
            onClick={openSignupModal}
          />
        )}

        <div className={classNames(styles.previewBlock, styles.column)}>
          <div className={styles.previewHeader}>
            <div className={styles.note}>
              {paidTraffic ? 'part c.' : 'part b.'}
            </div>
            <div className={styles.heading}>
              Your website traffic visitor list
            </div>
            <div className={styles.subHeading}>
              Here is a preview of what your anonymous website visitor contact
              list would look like every month!
            </div>
          </div>
          <div className={styles.preview}>
            <img src={dashboardSrc} className={styles.previewImage} />
          </div>
          <PrimaryButton className={styles.button} onClick={openSignupModal}>
            Capture my {beautifyNumber(missedOrganicLeads)} lost leads
          </PrimaryButton>
        </div>

        <HowXRayWorks />

        <div className={styles.timer}>
          {!isMobile && <CurvedBg className={styles.curvedBg} />}
          <div className={classNames(styles.row, styles.timerContent)}>
            <div className={styles.left}>
              <div
                className={classNames(
                  styles.column,
                  styles.alignStart,
                  styles.gap12,
                )}
              >
                <div className={styles.note}>greatness awaits</div>
                <div className={styles.heading}>
                  Every visitor is a potential lead!
                </div>
                <div className={styles.subHeading}>
                  This clock was activated at {moment().format('h:mm:ss a')} on{' '}
                  {moment(dateNow).format('MMMM Do YYYY')} and represents leads
                  and their contact information you could have captured by
                  installing our tag on your website.
                </div>
              </div>
            </div>
            <div className={styles.right}>
              <MissedLeadsTimer
                totalTraffic={totalTrafficRef.current}
                onClick={openSignupModal}
              />
            </div>
          </div>
        </div>

        <div
          className={classNames(
            styles.neilPatelBlock,
            styles.column,
            styles.gap40,
          )}
        >
          <div className={styles.heading}>
            What people are saying about MobileMonkey
          </div>
          <div
            className={classNames(
              isMobile ? styles.column : styles.row,
              isMobile ? styles.gap24 : styles.gap40,
            )}
          >
            <div className={styles.neilPatelImg}>
              <img src={neilPatel} />
            </div>
            <div className={classNames(styles.column, styles.gap8)}>
              <div className={styles.subHeading}>
                NEIL PATEL'S FAVORITE SALES OUTREACH AUTOMATION TOOL
              </div>
              <div className={styles.text}>
                Sales outreach automation results in radically improved website
                conversion rate and cost per lead. Think about what a 20% lead
                capture rate from digital ads, SEO and content marketing would
                do for your business growth. MobileMonkey X-Ray Lead Generator
                with automated sales outreach is a quantum leap in marketing
                technology.
              </div>
              <div
                className={classNames(
                  styles.row,
                  styles.alignCenter,
                  isMobile ? styles.gap24 : styles.gap40,
                )}
              >
                <img src={leaderLogo} className={styles.logo} />
                <img src={metaLogo} className={styles.logo} />
                <img src={martechLogo} className={styles.logo} />
                <img src={EntrepreneurLogo} className={styles.logo} />
              </div>
              <PrimaryButton
                className={styles.button}
                onClick={openSignupModal}
              >
                Capture my {beautifyNumber(missedOrganicLeads)} lost leads
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
